import React, {useEffect, useState} from "react";
import {Alert, Button, Checkbox, Descriptions, Form, Input, Modal, Radio, Space, Spin, Tag, Upload} from "antd";
import {message} from "../../utils/antdFeedback";
import {UploadOutlined, ArrowLeftOutlined} from "@ant-design/icons";
import {Wallet} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {AppDispatch, RootState} from "../../redux/store";
import {approvePaymentLinkLiquidationRequest, getPaymentLinkLiquidationRequestById, rejectPaymentLinkLiquidationRequest} from "../../redux/slice/paymentLinkSlice";
import {adminRequest} from "../../api/adminApi";
import {HeroHeader, Card, formatNaira, COLORS} from "../../designSystem";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../auth/adminPermissions";

// NGN is shown as ₦ (brand rule); any other currency keeps its ISO code.
const money = (value: any, currency = "NGN") =>
  !currency || currency === "NGN" ? formatNaira(value) : `${currency} ${Number(value || 0).toLocaleString()}`;

const statusLabel = (value: any, row?: any) => row?.statusText || ({
  1: "Pending admin approval",
  2: "Completed",
  3: "Approved",
  4: "Rejected",
  5: "Payout Pending",
} as Record<number, string>)[Number(value)] || String(value || "Unknown");

const statusColor = (value: any) => ({
  1: "gold",
  2: "green",
  3: "blue",
  4: "red",
  5: "purple",
} as Record<number, string>)[Number(value)] || "default";

function ApprovePaymentLinkLiquidation() {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {token, permissions, roles} = useSelector((state: RootState) => state.account);
  const canApprove = hasAdminPermission(permissions, roles, ADMIN_PERMISSIONS.paymentLinkLiquidationApprove);
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [manual, setManual] = useState(searchParams.get("method") === "manual");
  const request = useSelector((state: RootState) => state.paymentLinks.selectedLiquidationRequest);
  const [proofUrl, setProofUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isCurrentRequest = request && String(request.id) === String(id);
  const canComplete = canApprove && (Number(request?.status) === 1 || String(request?.status).toLowerCase() === "processing");

  useEffect(() => {
    setProofUrl("");
    form.resetFields();
    setManual(searchParams.get("method") === "manual");
  }, [id, form, searchParams]);

  useEffect(() => {
    if (token && id) {
      dispatch(getPaymentLinkLiquidationRequestById({token, requestId: id}));
    }
  }, [token, id, dispatch]);

  const uploadProof = async (file: any) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const result: any = await adminRequest("/file/document", {method: "POST", token, body: formData});
      const url = result?.returnedObject?.fileUrl;
      if (!url) throw new Error("No proof URL returned");
      setProofUrl(url);
      form.setFieldValue("paymentProofUrl", url);
      await form.validateFields(["paymentProofUrl"]);
      message.success("Proof uploaded");
    } catch {
      message.error("Upload failed");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const submit = async (values: any) => {
    if (!token || !id || submitting || uploading || !canComplete) return;
    setSubmitting(true);
    try {
      await dispatch(approvePaymentLinkLiquidationRequest({
        token,
        requestId: id,
        isManualTransfer: manual,
        externalReference: values.externalReference,
        paymentProofUrl: proofUrl,
        narration: values.narration,
      })).unwrap();
      message.success(manual ? "Manual transfer recorded and liquidation completed" : "Liquidation payout submitted and is pending confirmation");
      navigate("/payment-link-liquidations");
    } catch (error: any) {
      const errorMessage = error?.message || error || "Approval failed";
      Modal.error({
        title: "Payment link liquidation failed",
        content: errorMessage,
        okText: "Close",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reject = () => {
    Modal.confirm({
      title: "Reject liquidation request",
      content: (
        <Form id="reject-liquidation-form" layout="vertical" onFinish={async (values) => {
          if (!token || !id) return;
          await dispatch(rejectPaymentLinkLiquidationRequest({token, requestId: id, reason: values.reason})).unwrap();
          message.success("Liquidation rejected");
          navigate("/payment-link-liquidations");
        }}>
          <Form.Item name="reason" label="Reason" rules={[{required: true, message: "Enter a rejection reason"}]}>
            <Input.TextArea rows={3}/>
          </Form.Item>
        </Form>
      ),
      okText: "Reject",
      okButtonProps: {danger: true, htmlType: "submit", form: "reject-liquidation-form"},
    });
  };

  if (!isCurrentRequest) {
    return <AdminLayout><div className="m-10"><Spin tip="Loading request..."/></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <div className="mx-auto max-w-3xl">
          <Button className="mb-4" icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}>Back</Button>
          <HeroHeader
            icon={Wallet}
            title="Complete Payment Link Payout"
            subtitle="Pay through VFD or record a bank transfer already made to the beneficiary."
          />
          <Card className="mt-8 p-5">
            <Descriptions bordered column={1} className="mb-8">
              <Descriptions.Item label="Creator">{request.creatorEmail}</Descriptions.Item>
              <Descriptions.Item label="Amount"><span className="font-mono">{money(request.amount, request.currency)}</span></Descriptions.Item>
              <Descriptions.Item label="Bank">{request.bankName}</Descriptions.Item>
              <Descriptions.Item label="Account">{request.accountName} {request.accountNumber}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={statusColor(request.status)}>{statusLabel(request.status, request)}</Tag></Descriptions.Item>
              <Descriptions.Item label="PayHive Reference">{request.transactionReference}</Descriptions.Item>
              {request.externalReference && <Descriptions.Item label="External Reference">{request.externalReference}</Descriptions.Item>}
              {request.completedAt && <Descriptions.Item label="Completed At">{new Date(request.completedAt).toLocaleString()}</Descriptions.Item>}
              {request.narration && <Descriptions.Item label="Narration">{request.narration}</Descriptions.Item>}
              {request.paymentProofUrl && <Descriptions.Item label="Payment Proof"><a href={request.paymentProofUrl} target="_blank" rel="noreferrer">View payment proof</a></Descriptions.Item>}
            </Descriptions>
            {canComplete ? (
              <Form form={form} layout="vertical" onFinish={submit}>
                <Form.Item label="Transfer method">
                  <Radio.Group value={manual ? "manual" : "vfd"} disabled={submitting || uploading} onChange={(event) => {
                    setManual(event.target.value === "manual");
                    form.setFieldValue("confirmedTransfer", false);
                  }}>
                    <Radio value="vfd">Automatic transfer (VFD)</Radio>
                    <Radio value="manual">Manual transfer</Radio>
                  </Radio.Group>
                </Form.Item>
                <Alert className="mb-5" type="info" showIcon title={manual
                  ? "Record a transfer already made to the beneficiary shown above. This completes the request without sending another payment."
                  : "This submits a new VFD transfer. The payout stays pending until the bank confirms it."}/>
                <Form.Item label={manual ? "Bank transfer reference" : "External Reference"} name="externalReference"
                  rules={[{required: manual, whitespace: true, message: "Enter the bank transfer reference"}]}>
                  <Input maxLength={150} disabled={submitting} placeholder={manual ? "Reference from the completed bank transfer" : "Optional, VFD reference will be used when blank"}/>
                </Form.Item>
                <Form.Item label="Narration" name="narration">
                  <Input.TextArea rows={3} maxLength={490} disabled={submitting}/>
                </Form.Item>
                <Form.Item label="Payment Proof" required={manual}>
                  <Upload beforeUpload={uploadProof} showUploadList={false} disabled={submitting || uploading}>
                    <Button icon={<UploadOutlined/>} loading={uploading} disabled={submitting}>Upload Proof</Button>
                  </Upload>
                  {proofUrl && <a className="mt-2 block" href={proofUrl} target="_blank" rel="noreferrer">View uploaded proof</a>}
                </Form.Item>
                <Form.Item name="paymentProofUrl" hidden rules={[{required: manual, message: "Upload payment proof"}]}><Input/></Form.Item>
                {manual && <Form.Item name="confirmedTransfer" valuePropName="checked" rules={[{validator: (_, value) => value
                  ? Promise.resolve() : Promise.reject(new Error("Confirm that the transfer has been completed"))}]}>
                  <Checkbox disabled={submitting}>I confirm that {money(request.amount, request.currency)} has been transferred to {request.accountName} ({request.accountNumber}) at {request.bankName}.</Checkbox>
                </Form.Item>}
                {manual && !proofUrl && <p className="mb-4 text-sm text-slate-500">Payment proof is required to record a manual transfer.</p>}
                <Space>
                  <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting || uploading || (manual && !proofUrl)}>
                    {manual ? "Record manual transfer" : "Send via VFD"}
                  </Button>
                  <Button danger disabled={submitting || uploading} onClick={reject}>Reject</Button>
                </Space>
              </Form>
            ) : (
              <div className="rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                This liquidation request is {statusLabel(request.status, request).toLowerCase()} and no further payout action is available.
              </div>
            )}
          </Card>
        </div>
      </main>
    </AdminLayout>
  );
}

export default ApprovePaymentLinkLiquidation;
